/**
 * ListingsSearchBar — the location/listing omnibox in the listings browse
 * navbar (the center slot of `NavbarShell` on `/listings/rent` & `/listings/buy`).
 *
 * Reuses the shared `SearchBar`, pinned to the CURRENT browse mode so selecting
 * a district/estate STAYS on the active mode (`/listings/rent?district_id=…` vs
 * `/listings/buy?…`). The mode is derived from the URL segment — the route path
 * is the SSOT for which browse page we're on.
 *
 * ★ It lives beside `NavbarShell` rather than under
 * `features/storefront/listings/components/` for the reason `MarketingNavbar`
 * (now `SiteNavbar`) was moved here before it: it IS page chrome — the content
 * of the navbar's centre slot — and with `PageShell` composing chrome from one
 * table, the navbar is assembled in `infra` and `infra -> feature` is refused
 * by the DAG. The remedy for that refusal is this move, never a wider DAG.
 */
import { SearchBar } from '@/shared/components/search';
import { usePathname } from '@/shared/i18n/routing';
import { routes, type ListingsMode } from '@letsgethome/routes';

/** Current listings mode from the URL segment (`/listings/buy` → `buy`, else
 * `rent`). The buy-mode path comes from the routes builder — renaming the
 * listings route in the SSOT moves this check with it (R5 PR-5). */
function useListingsMode(): ListingsMode {
  return usePathname().includes(routes.listings.buy()) ? 'buy' : 'rent';
}

export function ListingsSearchBar() {
  const mode = useListingsMode();
  return <SearchBar variant="navbar" mode={mode} />;
}
